import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { AdminLayout } from '../components/AdminLayout.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { Field } from '../components/ui/field.js';
import { Input } from '../components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/deadlines')({
  component: AdminDeadlines,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

const LEVEL_LABEL: Record<string, string> = {
  overdue: 'Überfällig',
  critical: 'Kritisch (≤ 7 T.)',
  warning: 'Warnung (≤ 30 T.)',
  info: 'Info (≤ 90 T.)',
};

const SOURCE_LABEL: Record<string, string> = {
  manual: 'Stichtag',
  residence_permit: 'Aufenthaltstitel',
  max_duration: 'Höchstüberlassung',
  client_confirmation: 'Kundenbestätigung',
  calculator_catalog: 'Rechner-Katalog',
};

const KIND_OPTIONS = [
  { value: 'wage_floor_increase', label: 'Mindestlohn-Erhöhung' },
  { value: 'regulation_expiry', label: 'Verordnungs-Ende' },
  { value: 'tariff_expiry', label: 'Tarif-Ende' },
  { value: 'custom', label: 'Sonstiger Stichtag' },
] as const;

function levelClass(level: string): string {
  if (level === 'overdue') return 'text-[oklch(0.5_0.21_25)] font-bold';
  if (level === 'critical') return 'text-[oklch(0.55_0.2_25)] font-semibold';
  if (level === 'warning') return 'text-[oklch(0.6_0.15_85)]';
  return 'text-[var(--muted-foreground)]';
}

function AdminDeadlines() {
  const utils = trpc.useUtils();
  const overview = trpc.deadlines.overview.useQuery({});
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [kind, setKind] = useState<(typeof KIND_OPTIONS)[number]['value']>('custom');

  const create = trpc.deadlines.create.useMutation({
    onSuccess: async () => {
      await utils.deadlines.overview.invalidate();
      setTitle('');
      setDueDate('');
      toast.success('Stichtag angelegt.');
    },
    onError: (error) => toast.error(error.message),
  });
  const resolve = trpc.deadlines.resolve.useMutation({
    onSuccess: async () => {
      await utils.deadlines.overview.invalidate();
      toast.success('Frist als erledigt markiert.');
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <AdminLayout>
      <div className="space-y-5">
        <header>
          <h1 className="text-3xl font-bold">Fristen</h1>
          <p className="max-w-4xl text-[var(--muted-foreground)]">
            Alles mit Ablaufdatum an einem Ort: Aufenthaltstitel, Höchstüberlassungsdauer
            (18 Monate), überfällige Kundenbestätigungen und manuell gepflegte Stichtage
            (Mindestlohn-Erhöhungen, Verordnungs-/Tarif-Enden). Einsätze mit abgelaufenem
            Aufenthaltstitel werden bei der Aktivierung automatisch blockiert.
          </p>
        </header>

        {overview.data && (
          <div className="grid gap-3 sm:grid-cols-4">
            {(['overdue', 'critical', 'warning', 'info'] as const).map((level) => (
              <Card key={level}>
                <CardHeader>
                  <CardDescription>{LEVEL_LABEL[level]}</CardDescription>
                  <CardTitle className={`text-3xl ${levelClass(level)}`}>
                    {overview.data.counts[level]}
                  </CardTitle>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Neuer Stichtag</CardTitle>
            <CardDescription>
              Für Termine, die nicht aus den Daten ableitbar sind — z. B. „Lohnuntergrenze
              steigt auf 15,87 €" (01.04.2027) oder „Maler-Verordnung läuft aus" (30.06.2027).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-4 md:grid-cols-4"
              onSubmit={(event) => {
                event.preventDefault();
                create.mutate({ kind, title, dueDate });
              }}
            >
              <Field label="Art" htmlFor="deadline-kind">
                <Select value={kind} onValueChange={(value) => setKind(value as typeof kind)}>
                  <SelectTrigger id="deadline-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KIND_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Titel" htmlFor="deadline-title">
                <Input
                  id="deadline-title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="z. B. LUG steigt auf 15,87 €"
                  required
                />
              </Field>
              <Field label="Fällig am" htmlFor="deadline-due">
                <Input
                  id="deadline-due"
                  type="date"
                  value={dueDate}
                  onChange={(event) => setDueDate(event.target.value)}
                  required
                />
              </Field>
              <div className="flex items-end">
                <Button type="submit" disabled={create.isPending}>
                  {create.isPending ? 'Speichere …' : 'Anlegen'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Übersicht (90-Tage-Horizont)</CardTitle>
            <CardDescription>
              Sortiert nach Dringlichkeit. Abgeleitete Fristen verschwinden automatisch, wenn
              die Ursache behoben ist (z. B. neuer Aufenthaltstitel eingetragen).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {overview.isLoading && <p className="text-sm">Lade Fristen …</p>}
            {overview.error && (
              <p className="text-sm text-[oklch(0.55_0.2_25)]">{overview.error.message}</p>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stufe</TableHead>
                  <TableHead>Art</TableHead>
                  <TableHead>Frist</TableHead>
                  <TableHead>Fällig</TableHead>
                  <TableHead className="text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overview.data?.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                      Keine Fristen in den nächsten 90 Tagen. 🎉
                    </TableCell>
                  </TableRow>
                )}
                {overview.data?.items.map((item, index) => (
                  <TableRow key={`${item.source}-${item.deadlineId ?? item.workerId ?? item.timesheetId ?? index}`}>
                    <TableCell className={levelClass(item.level)}>
                      {LEVEL_LABEL[item.level]}
                    </TableCell>
                    <TableCell>{SOURCE_LABEL[item.source] ?? item.source}</TableCell>
                    <TableCell className="font-medium">{item.title}</TableCell>
                    <TableCell>
                      {item.dueDate}{' '}
                      <span className="text-xs text-[var(--muted-foreground)]">
                        ({item.daysLeft < 0 ? `seit ${-item.daysLeft} T.` : `in ${item.daysLeft} T.`})
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {item.deadlineId ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={resolve.isPending}
                          onClick={() => resolve.mutate({ id: item.deadlineId! })}
                        >
                          Erledigt
                        </Button>
                      ) : (
                        <span className="text-xs text-[var(--muted-foreground)]">abgeleitet</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
